/**
 * Lightweight toast system — module-level singleton so any component can call
 * pushToast() without prop drilling. ToastContainer subscribes and renders.
 *
 * Extracted from AdAccountsPage so lazy-imported modals can call pushToast
 * without their bundle pulling the entire page back in.
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, CircleAlert, Info } from "lucide-react";

export type ToastKind = "success" | "error" | "info";

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

let toastCounter = 0;
const toastSubs = new Set<(toasts: Toast[]) => void>();
let toastList: Toast[] = [];

export function pushToast(kind: ToastKind, message: string, durationMs = 3000) {
  const t: Toast = { id: ++toastCounter, kind, message };
  toastList = [...toastList, t];
  toastSubs.forEach((cb) => cb(toastList));
  window.setTimeout(() => {
    toastList = toastList.filter((x) => x.id !== t.id);
    toastSubs.forEach((cb) => cb(toastList));
  }, durationMs);
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  useEffect(() => {
    toastSubs.add(setToasts);
    setToasts(toastList);
    return () => { toastSubs.delete(setToasts); };
  }, []);
  // container ต้อง mount ตลอดเวลา — screen reader จะอ่าน aria-live ก็ต่อเมื่อ region
  // อยู่ใน DOM "ก่อน" เนื้อหาถูกใส่เข้าไป · ถ้า return null ตอนไม่มี toast จะพลาดทุกข้อความ
  return createPortal(
    // aria-live ให้ screen reader อ่าน toast โดยไม่แย่ง focus
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2"
    >
      {toasts.map((t) => {
        const Icon =
          t.kind === "success" ? CheckCircle2 : t.kind === "error" ? CircleAlert : Info;
        // โทนเดียวกับ Toast ของ kkautoweb — พื้นทึบ ขอบบาง ไอคอนสีเข้ม
        const tone =
          t.kind === "success"
            ? { wrap: "border-green-200 bg-green-50 text-green-800", icon: "text-green-600" }
            : t.kind === "error"
              ? { wrap: "border-red-200 bg-red-50 text-red-800", icon: "text-red-600" }
              : { wrap: "border-brand-200 bg-brand-50 text-brand-800", icon: "text-brand-600" };
        return (
          <div
            key={t.id}
            className={`pointer-events-auto flex items-start gap-2.5 rounded-xl border px-4 py-3 text-sm leading-snug shadow-card ${tone.wrap}`}
          >
            <Icon aria-hidden className={`h-5 w-5 shrink-0 ${tone.icon}`} />
            <span className="min-w-0 flex-1 break-words">{t.message}</span>
          </div>
        );
      })}
    </div>,
    document.body
  );
}
